<template>
  <div class="dsPageContainer">
    <section class="leftSection">
      <img :src="loginImg" alt="Login Image" class="loginLeftImg" />
    </section>

    <section class="rightSectionLogin">
      <img :src="logoImg" alt="logo" class="logo" />

      <div class="rightSectionMiddlePart">
        <p class="loginHeader">Welcome Back to Omni Channel</p>
        <p class="loginSubHeader">
          Effortlessly take control of your Chat bots with our next-generation
          Bot management platform.
        </p>

        <form class="loginForm" id="loginForm" @submit.prevent="login">
          <div class="dsFieldContainer">
            <label class="dsLabel">User Name</label>
            <input
              v-model="username"
              type="text"
              placeholder="Enter your email"
              class="dsInput"
            />
          </div>

          <div class="dsFieldContainer">
            <label class="dsLabel">Password</label>
            <div style="display: flex; gap: 8px; align-items: center;">
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Enter your password"
                class="dsInput"
              />
              <button type="button" @click="showPassword = !showPassword">
                {{ showPassword ? "Hide" : "Show" }}
              </button>
            </div>
          </div>

          <div class="dsFieldContainer lastFieldContainer">
            <label class="dsLabel">
              <input type="checkbox" v-model="rememberMe" />
              Remember me
            </label>
            <a href="#" class="forgotPasswordLink">Forgot Password?</a>
          </div>

          <div class="dsCaptchaContainer">
            <canvas ref="captchaCanvas" width="200" height="60"></canvas>

            <input
              v-model="captchaInput"
              type="text"
              placeholder="Enter captcha"
              class="dsInput"
            />

            <button @click="generateCaptcha" type="button">Refresh</button>
          </div>

          <p id="message" class="dsCaptchaMsg">{{ captchaMessage }}</p>

          <button class="ds_btn-login" type="submit" :disabled="loading">
            {{ loading ? "Loading..." : "Login" }}
          </button>

          <p v-if="err" class="err">{{ err }}</p>
        </form>
      </div>

      <p class="rightSectionBottomPart">
        Don't you have an account?
        <a href="#" class="signUpLink">Sign Up</a>
      </p>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from "vue";
import { useRouter } from "vue-router";
import { auth } from "@/services/auth";
import { useAgentSocket } from "@/services/socket";
import "@/assets/css/login.css";

import loginImg from "@/assets/images/login.svg";
import logoImg from "@/assets/images/slogo.svg";
import VARS from "@/services/vars"

const router = useRouter();
const { connect } = useAgentSocket();

const username = ref("");
const password = ref("");
const loading = ref(false);
const err = ref("");

const rememberMe = ref(true);
const showPassword = ref(false);

// captcha
const captchaCanvas = ref(null);
const captchaCode = ref("");
const captchaInput = ref("");
const captchaMessage = ref("");

function randomCaptcha(length = 5) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < length; i++) {
    out += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return out;
}

function generateCaptcha() {
  captchaCode.value = randomCaptcha();
  captchaMessage.value = "";

  const canvas = captchaCanvas.value;
  if (!canvas) return;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = "#f4f6fb";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.font = "28px Arial";
  ctx.fillStyle = "#1172FA";
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  for (let i = 0; i < captchaCode.value.length; i++) {
    const ch = captchaCode.value[i];
    const x = 20 + i * 32;
    const y = 38 + Math.random() * 8;
    const angle = (Math.random() - 0.5) * 0.4;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.fillText(ch, 0, 0);
    ctx.restore();
  }

  for (let i = 0; i < 5; i++) {
    ctx.strokeStyle = "rgba(17,114,250,0.3)";
    ctx.beginPath();
    ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
    ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
    ctx.stroke();
  }
}

function persistAuth(data) {
  auth.token = data.token;
  auth.user = data.user;

  localStorage.setItem("token", data.token);
  localStorage.setItem("user", JSON.stringify(data.user));

  if (rememberMe.value) {
    localStorage.setItem("remember_username", username.value);
  } else {
    localStorage.removeItem("remember_username");
  }
}

function redirectByRole(user) {
  const role = String(user?.role || "").toLowerCase();

  switch (role) {
    case "admin":
      router.push("/admin/dashboard");
      break;
    case "manager":
      router.push("/manager");
      break;
    case "agent":
    default:
      router.push("/inbox");
      break;
  }
}

async function login() {
  if (!username.value.trim()) {
    err.value = "Please enter username or email";
    return;
  }

  if (!password.value.trim()) {
    err.value = "Please enter password";
    return;
  }

  if (!captchaInput.value.trim()) {
    err.value = "Please enter captcha";
    return;
  }

  if (
    captchaInput.value.trim().toUpperCase() !== captchaCode.value.toUpperCase()
  ) {
    err.value = "Invalid captcha";
    captchaMessage.value = "Captcha does not match";
    generateCaptcha();
    captchaInput.value = "";
    return;
  }

  loading.value = true;
  err.value = "";
  captchaMessage.value = "";
  
  try {
    const r = await fetch(VARS.API_URL+"/api/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username: username.value.trim(),
        password: password.value,
      }),
    });

    let data = {};
    try {
      data = await r.json();
    } catch {
      throw new Error("Invalid server response");
    }

    if (!r.ok || !data.ok) {
      err.value = data.message || "Login failed";
      generateCaptcha();
      captchaInput.value = "";
      return;
    }

    persistAuth(data);

    try {
      connect();
    } catch (socketErr) {
      console.error("Socket connection failed:", socketErr);
    }

    redirectByRole(data.user);
  } catch (e) {
    console.error(e);
    err.value = e.message || "Network error";
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  const savedUsername = localStorage.getItem("remember_username");
  if (savedUsername) {
    username.value = savedUsername;
  }

  await nextTick();
  generateCaptcha();
});
</script>

<style scoped>
.err {
  color: #e74c3c;
  margin-top: 10px;
}
</style>